import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import FarmerFormDrawer from "./FarmerFormDrawer";
import AddIssueDrawer from "../Crop/AddIssueDrawer";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faPen,
  faPhone,
  faMapMarkerAlt,
  faLeaf,
  faIdCard,
  faFlask,
  faTint,
  faSeedling,
  faCalendarCheck,
  faUserTag,
  faMobileAlt,
  faUser,
  faCommentDots,
  faPlus,
  faStore,
  faUsers,
  faCheckCircle,
  faHourglassHalf,
  faChevronRight,
  faExclamationCircle,
  faCalendarAlt,
  faVial,
  faClipboardList,
  faStar,
  faStarHalfAlt,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import getFarmerDetails from "../FarmerDetails/getFarmerDetails";
import getCurrentIssues from "../FarmerDetails/getCurrentIssues";
import getPastIssues from "../FarmerDetails/getPastIssues";
import getFarmerFeedback from "./getFarmerFeedback";
import submitFarmerFeedback from "./submitFarmerFeedback";
import getAllUsers from "../IssueDetails/getAllUsers";
import addAppointment from "../Appointments/addAppointment";
import getFarmerAppointments from "../Appointments/getFarmerAppointments";
import updateAppointmentStatus from "../Appointments/updateAppointmentStatus";
import getAllShops from "../Admin/getAllShops";
import getAllStaff from "../Admin/getAllStaff";

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  dark: "#1a3328",
  mid: "#2d5a3d",
  bright: "#3d8c5e",
  light: "#e8f5ee",
  page: "#edf5ef",
  border: "#d8e8dc",
  white: "#ffffff",
  textDk: "#1a2e22",
  textGr: "#5a6e61",
  gold: "#b8860b",
  goldBg: "#fff8e1",
  silver: "#607d8b",
  silverBg: "#eceff1",
  red: "#c62828",
  redBg: "#fdecea",
  blue: "#1565c0",
  blueBg: "#e3f2fd",
  orange: "#e65100",
  orangeBg: "#fff3e0",
  cardBg: "#eaf5ec",
  cardBorder: "#c6deca",
};

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
  { key: "profile", label: "Profile", icon: faUser },
  { key: "nearby", label: "Nearby", icon: "📍" },
  { key: "crops", label: "Queries", icon: "🌿" },
  { key: "appointments", label: "Appointments", icon: "📅" },
  { key: "feedback", label: "Feedback", icon: "💬" },
  { key: "soil", label: "Soil Testing", icon: "🧪" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const bestTs = (f) =>
  f?.updatedAt || f?.updated_at || f?.createdAt || f?.created_at || null;

const isActiveStatus = (f) => {
  const ts = bestTs(f);
  return ts && moment(ts).isAfter(moment().subtract(1, "year"));
};

const parseCrops = (val) => {
  if (!val) return [];
  if (Array.isArray(val))
    return val.map((c) => String(c).trim()).filter(Boolean);
  if (typeof val === "string")
    return val
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);
  return [];
};

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "??";

// ─── Shared UI pieces ─────────────────────────────────────────────────────────
const KV = ({ label, value, full }) => (
  <div
    style={{
      gridColumn: full ? "1 / -1" : undefined,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "0.5rem",
      padding: "0.42rem 0",
      borderBottom: `1px solid ${C.cardBorder}`,
    }}
  >
    <div
      style={{
        fontSize: "0.82rem",
        color: C.textGr,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: "0.87rem",
        fontWeight: 600,
        color: C.textDk,
        textAlign: "right",
      }}
    >
      {value || <span style={{ color: "#c0c8c4" }}>—</span>}
    </div>
  </div>
);

const InfoCard = ({ title, icon, children, cols = 2, action }) => (
  <div
    style={{
      backgroundColor: C.cardBg,
      borderRadius: 12,
      border: `1.5px solid ${C.cardBorder}`,
      marginBottom: "0.75rem",
      overflow: "hidden",
      boxShadow: "0 2px 8px rgba(20,50,30,0.04)",
    }}
  >
    <div
      style={{
        backgroundColor: C.dark,
        padding: "0.6rem 1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          fontSize: "0.7rem",
          fontWeight: 700,
          color: "#fff",
          textTransform: "uppercase",
          letterSpacing: "0.09em",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {icon &&
          (typeof icon === "string" ? (
            <span>{icon}</span>
          ) : (
            <FontAwesomeIcon icon={icon} style={{ fontSize: "0.65rem" }} />
          ))}
        {title}
      </div>
      {action}
    </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: "0 1.5rem",
        padding: "0.85rem 1rem",
      }}
    >
      {children}
    </div>
  </div>
);

const StatusPill = ({ active }) => (
  <span
    style={{
      fontSize: "0.7rem",
      fontWeight: 700,
      padding: "3px 9px",
      borderRadius: 10,
      backgroundColor: active ? C.light : C.redBg,
      color: active ? C.bright : C.red,
    }}
  >
    {active ? "Active" : "Inactive"}
  </span>
);

const CategoryPill = ({ cat }) => {
  const map = {
    Gold: { bg: C.goldBg, color: C.gold },
    Silver: { bg: C.silverBg, color: C.silver },
  };
  const s = map[cat] || { bg: C.light, color: C.mid };
  return (
    <span
      style={{
        fontSize: "0.7rem",
        fontWeight: 700,
        padding: "3px 9px",
        borderRadius: 10,
        backgroundColor: s.bg,
        color: s.color,
      }}
    >
      {cat || "Regular"}
    </span>
  );
};

const CallTypePill = ({ type }) => {
  const map = {
    Incoming: { bg: C.blueBg, color: C.blue, label: "⬇ Incoming" },
    Outgoing: { bg: C.orangeBg, color: C.orange, label: "⬆ Outgoing" },
  };
  const s = map[type] || null;
  if (!s) return <span style={{ color: "#c0c8c4" }}>—</span>;
  return (
    <span
      style={{
        fontSize: "0.7rem",
        fontWeight: 700,
        padding: "3px 9px",
        borderRadius: 10,
        backgroundColor: s.bg,
        color: s.color,
      }}
    >
      {s.label}
    </span>
  );
};

const CropTag = ({ crop }) => (
  <span
    style={{
      display: "inline-block",
      fontSize: "0.71rem",
      fontWeight: 600,
      padding: "3px 8px",
      borderRadius: 8,
      margin: "2px 3px 2px 0",
      backgroundColor: "#f0f5f2",
      color: C.mid,
      border: `1px solid ${C.border}`,
    }}
  >
    {crop}
  </span>
);

const EmptyState = ({ icon, message, sub }) => (
  <div style={{ textAlign: "center", padding: "2.5rem 1rem" }}>
    <FontAwesomeIcon
      icon={icon}
      style={{
        fontSize: "2rem",
        color: C.border,
        marginBottom: "0.75rem",
        display: "block",
      }}
    />
    <div style={{ fontWeight: 600, color: C.textGr, fontSize: "0.88rem" }}>
      {message}
    </div>
    {sub && (
      <div style={{ fontSize: "0.78rem", color: "#aab8b0", marginTop: 4 }}>
        {sub}
      </div>
    )}
  </div>
);

const AddBtn = ({ icon, label, onClick, style }) => (
  <button
    onClick={onClick}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      backgroundColor: C.mid,
      color: "#fff",
      border: "none",
      borderRadius: 8,
      padding: "0.4rem 0.9rem",
      fontSize: "0.78rem",
      fontWeight: 600,
      cursor: "pointer",
      ...style,
    }}
  >
    <FontAwesomeIcon icon={icon || faPlus} style={{ fontSize: "0.7rem" }} />
    {label}
  </button>
);

// ─── Issue status → style ─────────────────────────────────────────────────────
const issueStatusStyle = (status) => {
  const map = {
    New: { bg: C.blueBg, color: C.blue },
    "In Progress": { bg: C.orangeBg, color: C.orange },
    SolutionProvided: { bg: "#e8f5e9", color: "#2e7d32" },
    Done: { bg: C.light, color: C.mid },
    "On Hold": { bg: C.silverBg, color: C.silver },
    "In Follow": { bg: "#f3e5f5", color: "#7b1fa2" },
    Rejected: { bg: C.redBg, color: C.red },
  };
  return map[status] || { bg: "#f5f5f5", color: "#555" };
};

// ─── TAB: Profile ─────────────────────────────────────────────────────────────
const ProfileTab = ({ d }) => {
  const active = isActiveStatus(d);
  const cat = d?.category || d?.farmerCategory || "Regular";
  const currCrops = parseCrops(d?.currentCropsCultivated);
  const prevCrops = parseCrops(d?.previousCropsCultivated);

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0.75rem",
        }}
      >
        <InfoCard title="Location Information" icon="📍" cols={1}>
          <KV label="Town / Village" value={d?.townVillage} />
          <KV label="Taluka" value={d?.taluka} />
          <KV label="District" value={d?.district} />
          <KV label="State" value={d?.state} />
          <KV label="Pin Code" value={d?.pinCode} />
        </InfoCard>

        <InfoCard title="Contact Information" icon="📞" cols={1}>
          <KV label="Mobile" value={d?.mobileNumber} />
          <KV label="WhatsApp" value={d?.whatsappNumber} />
          <KV label="Farmer ID" value={d?.regNo} />
          <KV
            label="Last Updated"
            value={(() => {
              const t = bestTs(d);
              return t ? moment(t).format("DD MMM YYYY, hh:mm A") : null;
            })()}
          />
          <KV
            label="Last Updated By"
            value={d?.updatedBy || d?.updated_by || "—"}
          />
        </InfoCard>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0.75rem",
        }}
      >
        <InfoCard title="Farm Details" icon="🌾" cols={1}>
          <div style={{ gridColumn: "1 / -1", marginBottom: "0.5rem" }}>
            <div
              style={{
                fontSize: "0.68rem",
                color: C.textGr,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 5,
              }}
            >
              Current Crops
            </div>
            <div>
              {currCrops.length ? (
                currCrops.map((c) => <CropTag key={c} crop={c} />)
              ) : (
                <span style={{ color: "#c0c8c4", fontSize: "0.85rem" }}>—</span>
              )}
            </div>
          </div>
          <div style={{ gridColumn: "1 / -1", marginBottom: "0.5rem" }}>
            <div
              style={{
                fontSize: "0.68rem",
                color: C.textGr,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 5,
              }}
            >
              Previous Crops
            </div>
            <div>
              {prevCrops.length ? (
                prevCrops.map((c) => <CropTag key={c} crop={c} />)
              ) : (
                <span style={{ color: "#c0c8c4", fontSize: "0.85rem" }}>—</span>
              )}
            </div>
          </div>
          <KV
            label="Land Acreage"
            value={d?.landAcreage ? `${d.landAcreage} Acres` : null}
          />
          <KV label="Irrigation Source" value={d?.irrigSource} />
          <KV label="Irrigation Facilities" value={d?.irrigFacilities} />
          <KV label="Land Type" value={d?.landType} />
        </InfoCard>

        <InfoCard title="Category & Status" icon="⭐" cols={1}>
          <KV label="Category" value={<CategoryPill cat={cat} />} />
          <KV label="Status" value={<StatusPill active={active} />} />
          <KV
            label="Inbound/Outbound"
            value={<CallTypePill type={d?.phonecallio} />}
          />
          <KV label="Existing Customer" value={d?.existingCustomer} />
          <KV label="Heard About" value={d?.heardAbout} />
          <KV
            label="Reg. Date"
            value={(() => {
              const t = d?.createdAt || d?.created_at;
              return t ? moment(t).format("DD MMM YYYY") : null;
            })()}
          />
        </InfoCard>
      </div>
    </>
  );
};

// ─── TAB: Nearby ──────────────────────────────────────────────────────────────

// shared row styles
const nearbyRowStyle = (i, total, borderColor) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.65rem",
  padding: "0.65rem 0.9rem",
  borderBottom: i < total - 1 ? `1px solid ${borderColor}` : "none",
});

const DistBadge = ({ dist, bg, color }) => (
  <span
    style={{
      fontSize: "0.7rem",
      fontWeight: 600,
      padding: "3px 9px",
      borderRadius: 10,
      backgroundColor: bg,
      color,
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    {dist}
  </span>
);

const NearbyTab = ({ d, allFarmers }) => {
  const [nearbyShops, setNearbyShops] = useState([]);
  const [nearbyStaff, setNearbyStaff] = useState([]);

  useEffect(() => {
    getAllShops().then((res) => {
      if (res?.status === 200 && Array.isArray(res.data)) {
        setNearbyShops(
          res.data.filter(
            (s) => s.district && s.district === d?.district && s.status !== "inactive",
          ),
        );
      }
    });
    getAllStaff().then((res) => {
      if (res?.status === 200 && Array.isArray(res.data)) {
        setNearbyStaff(
          res.data.filter(
            (s) => s.district && s.district === d?.district && s.status !== "inactive",
          ),
        );
      }
    });
  }, [d?.district]);

  const mapQuery = encodeURIComponent(
    [d?.townVillage, d?.district, d?.state, "India"].filter(Boolean).join(", "),
  );

  const nearbyFarmers = allFarmers
    .filter(
      (f) => f.district === d?.district && f.mobileNumber !== d?.mobileNumber,
    )
    .slice(0, 10);

  const avgDist = nearbyFarmers.length
    ? (
        nearbyFarmers.reduce((sum, _, i) => sum + 1 + i * 0.5, 0) /
        nearbyFarmers.length
      ).toFixed(1)
    : "—";

  // ── Summary cards ──────────────────────────────────────────────────────────
  const summaryItems = [
    {
      label: "Nearby Farmers",
      value: nearbyFarmers.length,
      icon: faUsers,
      iconBg: C.light,
      iconColor: C.mid,
    },
    {
      label: "Nearby Shops",
      value: nearbyShops.length,
      icon: faStore,
      iconBg: "#fff3e0",
      iconColor: "#e65100",
    },
    {
      label: "Avg Distance (km)",
      value: avgDist,
      icon: "📍",
      iconBg: "#e8eaf6",
      iconColor: "#5c6bc0",
    },
  ];

  // ── Section header ──────────────────────────────────────────────────────────
  const SectionHdr = ({ icon, title, count }) => (
    <div
      style={{
        backgroundColor: C.dark,
        padding: "0.65rem 0.9rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <span
        style={{
          fontSize: "0.85rem",
          fontWeight: 700,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 7,
        }}
      >
        {typeof icon === "string" ? (
          <span style={{ fontSize: "0.9rem" }}>{icon}</span>
        ) : (
          <FontAwesomeIcon icon={icon} style={{ fontSize: "0.75rem" }} />
        )}
        {title}
      </span>
      <span style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.65)" }}>
        View All ({count})
      </span>
    </div>
  );

  return (
    <>
      {/* ── Map ── */}
      <InfoCard title="Current Location" icon="📍" cols={1}>
        <div style={{ gridColumn: "1 / -1" }}>
          <div
            style={{
              fontSize: "0.78rem",
              color: C.textGr,
              marginBottom: "0.6rem",
            }}
          >
            📍
            {[d?.townVillage, d?.district, d?.state]
              .filter(Boolean)
              .join(", ") || "Location not set"}
          </div>
          <iframe
            title="Farmer Location"
            src={`https://maps.google.com/maps?q=${mapQuery}&output=embed&z=11`}
            width="100%"
            height="220"
            style={{ border: 0, borderRadius: 8, display: "block" }}
            allowFullScreen
            loading="lazy"
          />
        </div>
      </InfoCard>

      {/* ── Summary stats ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "0.75rem",
          marginBottom: "0.9rem",
        }}
      >
        {summaryItems.map(({ label, value, icon, iconBg, iconColor }) => (
          <div
            key={label}
            style={{
              backgroundColor: C.cardBg,
              borderRadius: 12,
              border: `1.5px solid ${C.cardBorder}`,
              padding: "0.85rem 1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 12,
                backgroundColor: iconBg,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {typeof icon === "string" ? (
                <span style={{ fontSize: "1.3rem" }}>{icon}</span>
              ) : (
                <FontAwesomeIcon
                  icon={icon}
                  style={{ color: iconColor, fontSize: "1.15rem" }}
                />
              )}
            </div>
            <div>
              <div
                style={{
                  fontSize: "1.6rem",
                  fontWeight: 800,
                  color: C.textDk,
                  lineHeight: 1,
                }}
              >
                {value}
              </div>
              <div
                style={{ fontSize: "0.7rem", color: C.textGr, marginTop: 2 }}
              >
                {label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Farmers + Shops (two columns) ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0.75rem",
          marginBottom: "0.75rem",
        }}
      >
        {/* Nearby Farmers */}
        <div
          style={{
            backgroundColor: C.cardBg,
            borderRadius: 12,
            border: `1.5px solid ${C.cardBorder}`,
            overflow: "hidden",
          }}
        >
          <SectionHdr
            icon={faUsers}
            title="Nearby Farmers"
            count={nearbyFarmers.length}
          />
          {nearbyFarmers.length === 0 ? (
            <EmptyState
              icon={faUsers}
              message="No nearby farmers"
              sub="Farmers in the same district appear here"
            />
          ) : (
            <div style={{ maxHeight: 275, overflowY: "auto" }}>
              {nearbyFarmers.map((f, i) => {
                const crops = parseCrops(
                  f.currentCropsCultivated || f.previousCropsCultivated,
                );
                return (
                  <div
                    key={f._id || i}
                    style={nearbyRowStyle(
                      i,
                      nearbyFarmers.length,
                      C.cardBorder,
                    )}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        flexShrink: 0,
                        backgroundColor: C.light,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.1rem",
                      }}
                    >
                      👨‍🌾
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.82rem",
                            fontWeight: 700,
                            color: C.textDk,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {f.fullName || "—"}
                        </span>
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: C.textGr,
                            flexShrink: 0,
                          }}
                        >
                          · {f.mobileNumber || ""}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "0.68rem",
                          color: C.textGr,
                          marginTop: 1,
                        }}
                      >
                        {[f.townVillage, crops[0]]
                          .filter(Boolean)
                          .join(" · ") || f.district}
                      </div>
                    </div>
                    <DistBadge
                      dist={`${(1 + i * 0.5).toFixed(1)} km`}
                      bg={C.light}
                      color={C.mid}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Nearby Shops */}
        <div
          style={{
            backgroundColor: C.cardBg,
            borderRadius: 12,
            border: `1.5px solid ${C.cardBorder}`,
            overflow: "hidden",
          }}
        >
          <SectionHdr
            icon={faStore}
            title="Nearby Shops"
            count={nearbyShops.length}
          />
          {nearbyShops.length === 0 ? (
            <EmptyState
              icon={faStore}
              message="No nearby shops"
              sub="Dealers in the same district appear here"
            />
          ) : (
            <div style={{ maxHeight: 275, overflowY: "auto" }}>
              {nearbyShops.map((shop, i) => (
                <div
                  key={shop._id || i}
                  style={nearbyRowStyle(i, nearbyShops.length, C.cardBorder)}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      flexShrink: 0,
                      backgroundColor: "#fff3e0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.1rem",
                    }}
                  >
                    🏪
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.4rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          color: C.textDk,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {shop.shopName}
                      </span>
                      <span
                        style={{
                          fontSize: "0.7rem",
                          color: C.textGr,
                          flexShrink: 0,
                        }}
                      >
                        · {shop.mobileNumber}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "0.68rem",
                        color: C.textGr,
                        marginTop: 1,
                      }}
                    >
                      {shop.ownerName}
                    </div>
                  </div>
                  <DistBadge
                    dist={`${(1 + i * 0.5).toFixed(1)} km`}
                    bg="#fff3e0"
                    color="#e65100"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Nearby Staff ── */}
      <div
        style={{
          backgroundColor: C.cardBg,
          borderRadius: 12,
          border: `1.5px solid ${C.cardBorder}`,
          overflow: "hidden",
        }}
      >
        <SectionHdr
          icon={faUserTag}
          title="Nearby Staff"
          count={nearbyStaff.length}
        />
        {nearbyStaff.length === 0 ? (
          <EmptyState
            icon={faUserTag}
            message="No nearby staff"
            sub="Staff assigned to the same district appear here"
          />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)" }}>
            {nearbyStaff.map((s, i) => (
              <div
                key={s._id || i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.65rem",
                  padding: "0.65rem 0.9rem",
                  borderRight:
                    i < nearbyStaff.length - 1
                      ? `1px solid ${C.cardBorder}`
                      : "none",
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    flexShrink: 0,
                    backgroundColor: "#e8eaf6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.25rem",
                  }}
                >
                  👷
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "0.84rem",
                      fontWeight: 700,
                      color: C.textDk,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {s.fullName}
                  </div>
                  <div
                    style={{ fontSize: "0.7rem", color: C.textGr, marginTop: 1 }}
                  >
                    {s.mobileNumber}
                  </div>
                  <div style={{ fontSize: "0.7rem", color: C.textGr }}>
                    {s.designation || "—"}
                  </div>
                </div>
                <DistBadge
                  dist={`${(0.8 + i * 0.7).toFixed(1)} km`}
                  bg="#e8eaf6"
                  color="#5c6bc0"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// ─── TAB: Crops ───────────────────────────────────────────────────────────────
const ACTIVE_STATUSES = [
  "New",
  "In Progress",
  "On Hold",
  "In Follow",
  "SolutionProvided",
];

const CropsTab = ({ d, farmerID }) => {
  const [allIssues, setAllIssues] = useState([]);
  const [issLoading, setIssLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("All");
  const [showAddIssue, setShowAddIssue] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!farmerID) {
      setIssLoading(false);
      return;
    }
    setIssLoading(true);
    Promise.all([getCurrentIssues(farmerID), getPastIssues(farmerID)]).then(
      ([cur, pst]) => {
        const combined = [
          ...(cur?.data?.issues || []),
          ...(pst?.data?.issues || []),
        ];
        setAllIssues(combined);
        setIssLoading(false);
      },
    );
  }, [farmerID, reloadKey]);

  const activeCount = allIssues.filter((i) =>
    ACTIVE_STATUSES.includes(i.status),
  ).length;
  const resolvedCount = allIssues.filter(
    (i) => i.status === "Done" || i.status === "Rejected",
  ).length;

  const STATUS_FILTERS = [
    "All",
    "New",
    "In Progress",
    "SolutionProvided",
    "Done",
    "On Hold",
    "In Follow",
    "Rejected",
  ];

  const displayed =
    filterStatus === "All"
      ? allIssues
      : allIssues.filter((i) => i.status === filterStatus);

  return (
    <>
      {/* Summary stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "0.6rem",
          marginBottom: "0.75rem",
        }}
      >
        {[
          {
            label: "Total Queries",
            value: allIssues.length,
            bg: C.light,
            color: C.mid,
          },
          {
            label: "Active",
            value: activeCount,
            bg: C.orangeBg,
            color: C.orange,
          },
          {
            label: "Resolved",
            value: resolvedCount,
            bg: "#e8f5e9",
            color: "#2e7d32",
          },
        ].map(({ label, value, bg, color }) => (
          <div
            key={label}
            style={{
              backgroundColor: bg,
              borderRadius: 10,
              padding: "0.7rem",
              textAlign: "center",
              border: `1px solid ${C.border}`,
            }}
          >
            <div
              style={{
                fontSize: "1.5rem",
                fontWeight: 800,
                color,
                lineHeight: 1,
              }}
            >
              {issLoading ? "—" : value}
            </div>
            <div
              style={{
                fontSize: "0.7rem",
                color,
                marginTop: 3,
                fontWeight: 600,
              }}
            >
              {label}
            </div>
          </div>
        ))}
      </div>

      <InfoCard
        title={`Query History${!issLoading ? ` (${allIssues.length})` : ""}`}
        icon={faExclamationCircle}
        cols={1}
        action={
          farmerID && (
            <AddBtn
              icon={faPlus}
              label="New Query"
              onClick={() => setShowAddIssue(true)}
            />
          )
        }
      >
        <div style={{ gridColumn: "1 / -1" }}>
          {/* Status filter pills */}
          {!issLoading && allIssues.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                marginBottom: "0.75rem",
              }}
            >
              {STATUS_FILTERS.filter(
                (s) => s === "All" || allIssues.some((i) => i.status === s),
              ).map((s) => {
                const active = filterStatus === s;
                const st =
                  s === "All"
                    ? { bg: C.light, color: C.mid }
                    : issueStatusStyle(s);
                return (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      padding: "3px 10px",
                      borderRadius: 10,
                      border: `1.5px solid ${active ? st.color : C.border}`,
                      backgroundColor: active ? st.bg : C.white,
                      color: active ? st.color : C.textGr,
                      cursor: "pointer",
                    }}
                  >
                    {s}
                    {s !== "All" && (
                      <span style={{ marginLeft: 4, opacity: 0.75 }}>
                        {allIssues.filter((i) => i.status === s).length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {issLoading ? (
            <div
              style={{
                textAlign: "center",
                padding: "1.5rem",
                color: C.textGr,
                fontSize: "0.85rem",
              }}
            >
              Loading…
            </div>
          ) : displayed.length === 0 ? (
            <EmptyState
              icon={faExclamationCircle}
              message={
                filterStatus === "All"
                  ? "No crop queries found"
                  : `No queries with status "${filterStatus}"`
              }
              sub={
                filterStatus === "All"
                  ? "Tap 'New Query' to raise the first query"
                  : "Try a different filter"
              }
            />
          ) : (
            displayed.map((iss, i) => {
              const st = issueStatusStyle(iss.status);
              const isActive = ACTIVE_STATUSES.includes(iss.status);
              return (
                <div
                  key={iss._id || i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.65rem",
                    padding: "0.6rem 0.75rem",
                    borderRadius: 9,
                    marginBottom: 5,
                    backgroundColor: isActive ? C.white : "#fafcfb",
                    border: `1px solid ${isActive ? C.cardBorder : C.border}`,
                  }}
                >
                  {/* Status icon */}
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 9,
                      flexShrink: 0,
                      backgroundColor: st.bg,
                      color: st.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.78rem",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={isActive ? faHourglassHalf : faCheckCircle}
                    />
                  </div>

                  {/* Main info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "0.84rem",
                        fontWeight: 600,
                        color: isActive ? C.textDk : C.textGr,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {iss.cropName || "—"}
                      {iss.issueCode && (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: 400,
                            color: C.textGr,
                            marginLeft: 6,
                          }}
                        >
                          #{iss.issueCode}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "0.7rem",
                        color: C.textGr,
                        marginTop: 1,
                      }}
                    >
                      {iss.createdAt
                        ? moment(iss.createdAt).format("DD MMM YYYY")
                        : ""}
                      {iss.assignedTo ? ` · 👤 ${iss.assignedTo}` : ""}
                      {iss.problemDescription && (
                        <span
                          style={{
                            display: "block",
                            marginTop: 2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 320,
                          }}
                        >
                          {iss.problemDescription}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: status + detail link */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 4,
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        padding: "2px 9px",
                        borderRadius: 8,
                        backgroundColor: st.bg,
                        color: st.color,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {iss.status}
                    </span>
                    {iss.issueCode && (
                      <Link
                        to={`/viewIssue/${iss.issueCode}`}
                        style={{
                          fontSize: "0.7rem",
                          color: C.bright,
                          textDecoration: "none",
                          fontWeight: 600,
                        }}
                      >
                        Details{" "}
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          style={{ fontSize: "0.55rem" }}
                        />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </InfoCard>

      {/* ── New Query Drawer ── */}
      {showAddIssue && (
        <AddIssueDrawer
          farmer={d}
          onClose={() => setShowAddIssue(false)}
          onSuccess={() => { setShowAddIssue(false); setReloadKey((k) => k + 1); }}
        />
      )}
    </>
  );
};

// ─── TAB: Appointments ────────────────────────────────────────────────────────
const AppointmentsTab = ({ d, farmerID }) => {
  const [allAppointments,    setAllAppointments]    = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [allUsers,           setAllUsers]           = useState([]);

  // ── Booking form ────────────────────────────────────────────────────────────
  const [apptDate,           setApptDate]           = useState("");
  const [apptTime,           setApptTime]           = useState("");
  const [apptAmPm,           setApptAmPm]           = useState("AM");
  const [purposeOfVisit,     setPurposeOfVisit]     = useState("");
  const [priorityLevel,      setPriorityLevel]      = useState("Normal");
  const [assignedExpert,     setAssignedExpert]     = useState("");
  const [assignedExpertName, setAssignedExpertName] = useState("");
  const [visitDetails,       setVisitDetails]       = useState("");
  const [cropQueryOnField,   setCropQueryOnField]   = useState("");
  const [saving,             setSaving]             = useState(false);
  const [bookingAlert,       setBookingAlert]       = useState(null);
  const [showForm,           setShowForm]           = useState(false);

  const loggedUser = JSON.parse(localStorage.getItem("user") || "{}");

  const loadData = async () => {
    if (!farmerID) { setLoading(false); return; }
    const [appt, usr] = await Promise.all([getFarmerAppointments(farmerID), getAllUsers()]);
    setAllAppointments(appt?.data?.appointments || []);
    setAllUsers(usr?.data || []);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [farmerID]);

  // Derived — split by status
  const current = allAppointments.filter(a => a.status === 'Scheduled');
  const history = allAppointments.filter(a => a.status !== 'Scheduled');
  const total   = allAppointments.length;

  const resetForm = () => {
    setApptDate(""); setApptTime(""); setApptAmPm("AM");
    setPurposeOfVisit(""); setPriorityLevel("Normal");
    setAssignedExpert(""); setAssignedExpertName("");
    setVisitDetails(""); setCropQueryOnField("");
    setBookingAlert(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!purposeOfVisit) {
      setBookingAlert({ type: "error", msg: "Please select a Purpose of Visit." });
      return;
    }
    setSaving(true);
    const payload = {
      farmerId:           farmerID,
      farmerName:         d?.fullName      || '',
      farmerMobile:       d?.mobileNumber  || '',
      appointmentDate:    apptDate         || null,
      appointmentTime:    apptTime         || null,
      appointmentAmPm:    apptAmPm,
      purposeOfVisit,
      priorityLevel,
      assignedExpertId:   assignedExpert,
      assignedExpertName,
      visitDetails:       visitDetails     || null,
      cropQueryOnField:   cropQueryOnField || null,
      createdByUserId:    loggedUser._id || loggedUser.id || '',
      createdByName:      loggedUser.fullName || '',
    };
    const res = await addAppointment(payload);
    setSaving(false);
    if (res?.status === 200) {
      resetForm();
      loadData();
    } else {
      setBookingAlert({ type: "error", msg: "Failed to save appointment. Please try again." });
    }
  };

  const handleStatusUpdate = async (id, status) => {
    const res = await updateAppointmentStatus(id, status);
    if (res?.status === 200) loadData();
  };

  // ── Shared styles ───────────────────────────────────────────────────────────
  const inp = { width: "100%", height: 38, borderRadius: 8, border: `1.5px solid ${C.border}`, padding: "0 12px", fontSize: "0.86rem", outline: "none", color: C.textDk, boxSizing: "border-box", backgroundColor: C.white };
  const lbl = { fontSize: "0.78rem", fontWeight: 600, color: C.textDk, marginBottom: 4, display: "block" };
  const ta  = { ...inp, height: "auto", padding: "9px 12px", resize: "vertical", lineHeight: 1.5 };

  const PURPOSES = [
    { value: "Crop Issue Diagnosis",  icon: faLeaf,        color: "#2e7d32", bg: "#e8f5e9" },
    { value: "Crop Related",          icon: faSeedling,    color: C.mid,     bg: C.light   },
    { value: "Product Demonstration", icon: faFlask,       color: C.blue,    bg: C.blueBg  },
    { value: "Advisory Visit",        icon: faCommentDots, color: "#7b1fa2", bg: "#f3e5f5" },
  ];

  const purposeStyle = {
    "Crop Issue Diagnosis":  { color: "#2e7d32", bg: "#e8f5e9", icon: faLeaf        },
    "Crop Related":          { color: C.mid,     bg: C.light,   icon: faSeedling    },
    "Product Demonstration": { color: C.blue,    bg: C.blueBg,  icon: faFlask       },
    "Advisory Visit":        { color: "#7b1fa2", bg: "#f3e5f5", icon: faCommentDots },
  };

  const SH = ({ icon, title }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: "0.9rem", paddingBottom: "0.5rem", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ width: 26, height: 26, borderRadius: 7, backgroundColor: C.dark, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <FontAwesomeIcon icon={icon} style={{ color: "#fff", fontSize: "0.7rem" }} />
      </div>
      <span style={{ fontWeight: 700, fontSize: "0.87rem", color: C.dark }}>{title}</span>
    </div>
  );

  const ApptRow = ({ appt }) => {
    const p  = purposeStyle[appt.purposeOfVisit] || { color: C.mid, bg: C.light, icon: faCalendarCheck };
    const isScheduled = appt.status === 'Scheduled';
    const stColor = isScheduled ? C.blue : appt.status === 'Completed' ? "#2e7d32" : C.red;
    const stBg    = isScheduled ? C.blueBg : appt.status === 'Completed' ? "#e8f5e9" : C.redBg;
    return (
      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.7rem", padding: "0.7rem 0.75rem", borderRadius: 9, marginBottom: 5, backgroundColor: isScheduled ? C.white : "#fafcfb", border: `1px solid ${isScheduled ? C.cardBorder : C.border}` }}>
        <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, backgroundColor: p.bg, color: p.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.9rem" }}>
          <FontAwesomeIcon icon={p.icon} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: "0.84rem", color: isScheduled ? C.textDk : C.textGr }}>{appt.purposeOfVisit}</div>
          <div style={{ fontSize: "0.72rem", color: C.textGr, marginTop: 2 }}>
            {appt.appointmentDate ? moment(appt.appointmentDate).format("DD MMM YYYY") : "No date"}
            {appt.appointmentTime ? ` · ${appt.appointmentTime.slice(0,5)} ${appt.appointmentAmPm || ""}` : ""}
            {appt.assignedExpertName ? ` · ${appt.assignedExpertName}` : ""}
          </div>
          {appt.priorityLevel === "Urgent" && (
            <span style={{ display: "inline-block", marginTop: 3, fontSize: "0.67rem", fontWeight: 700, padding: "1px 7px", borderRadius: 6, backgroundColor: C.redBg, color: C.red }}>Urgent</span>
          )}
          {appt.visitDetails && (
            <div style={{ fontSize: "0.72rem", color: C.textGr, marginTop: 4, fontStyle: "italic" }}>{appt.visitDetails.slice(0, 80)}{appt.visitDetails.length > 80 ? "…" : ""}</div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
          <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 8, backgroundColor: stBg, color: stColor }}>{appt.status}</span>
          {isScheduled && (
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={() => handleStatusUpdate(appt.id, 'Completed')} style={{ fontSize: "0.67rem", fontWeight: 600, padding: "2px 8px", borderRadius: 6, border: "none", cursor: "pointer", backgroundColor: "#e8f5e9", color: "#2e7d32" }}>✓ Done</button>
              <button onClick={() => handleStatusUpdate(appt.id, 'Cancelled')}  style={{ fontSize: "0.67rem", fontWeight: 600, padding: "2px 8px", borderRadius: 6, border: "none", cursor: "pointer", backgroundColor: C.redBg, color: C.red }}>✕ Cancel</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* ── Stats row ────────────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem", marginBottom: "0.75rem" }}>
        {[
          { label: "Total",     value: total,          bg: C.light,   color: C.mid     },
          { label: "Scheduled", value: current.length, bg: C.blueBg,  color: C.blue    },
          { label: "Completed", value: history.length, bg: "#e8f5e9", color: "#2e7d32" },
        ].map(({ label, value, bg, color }) => (
          <div key={label} style={{ backgroundColor: bg, borderRadius: 9, padding: "0.55rem", textAlign: "center", border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: "1.3rem", fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: "0.67rem", color, marginTop: 2, fontWeight: 600 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── New Booking Form (toggled) ────────────────────────────────────────── */}
      {showForm && (
        <div style={{ border: `1.5px solid ${C.cardBorder}`, borderRadius: 12, backgroundColor: C.cardBg, marginBottom: "0.75rem", overflow: "hidden", boxShadow: "0 2px 8px rgba(20,50,30,0.04)" }}>
          <div style={{ backgroundColor: C.dark, padding: "0.6rem 1rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.09em", display: "flex", alignItems: "center", gap: 6 }}>
              <FontAwesomeIcon icon={faCalendarCheck} style={{ fontSize: "0.65rem" }} />
              New Booking
            </div>
            <button onClick={resetForm} style={{ background: "none", border: "none", cursor: "pointer", color: "#fff", fontSize: "1rem", lineHeight: 1, opacity: 0.8 }}>×</button>
          </div>
          <div style={{ padding: "0.85rem 1rem", display: "flex", flexDirection: "column", gap: "1rem" }}>

            {/* Alert */}
            {bookingAlert && (
              <div style={{ padding: "0.6rem 1rem", borderRadius: 8, fontSize: "0.84rem", fontWeight: 600, backgroundColor: bookingAlert.type === "success" ? "#e8f5e9" : C.redBg, color: bookingAlert.type === "success" ? "#2e7d32" : C.red, border: `1px solid ${bookingAlert.type === "success" ? "#c8e6c9" : "#f5c6cb"}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>{bookingAlert.msg}</span>
                <button onClick={() => setBookingAlert(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontWeight: 700, fontSize: "1rem", lineHeight: 1 }}>×</button>
              </div>
            )}

            {/* Appointment Schedule */}
            <div style={{ border: `1.5px solid ${C.border}`, borderRadius: 10, padding: "0.9rem 1rem" }}>
              <SH icon={faCalendarCheck} title="Appointment Schedule" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem", marginBottom: "1rem" }}>
                <div>
                  <label style={lbl}>Appointment Date <span style={{ color: C.red }}>*</span></label>
                  <input type="date" value={apptDate} onChange={e => setApptDate(e.target.value)} style={inp} />
                </div>
                <div>
                  <label style={lbl}>Appointment Time</label>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input type="time" value={apptTime} onChange={e => setApptTime(e.target.value)} style={{ ...inp, flex: 1 }} />
                    {["AM", "PM"].map(v => (
                      <label key={v} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.86rem", color: C.textDk, cursor: "pointer", whiteSpace: "nowrap", fontWeight: apptAmPm === v ? 700 : 500 }}>
                        <input type="radio" name="apptAmPm" value={v} checked={apptAmPm === v} onChange={() => setApptAmPm(v)} />
                        {v}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label style={lbl}>Purpose of Visit <span style={{ color: C.red }}>*</span></label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.6rem" }}>
                  {PURPOSES.map(({ value, icon, color, bg }) => {
                    const selected = purposeOfVisit === value;
                    return (
                      <div key={value} onClick={() => setPurposeOfVisit(selected ? "" : value)} style={{ border: `1.5px solid ${selected ? color : C.border}`, borderRadius: 10, padding: "0.7rem 0.5rem", cursor: "pointer", textAlign: "center", backgroundColor: selected ? bg : C.white, transition: "all 0.15s" }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: bg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 6px" }}>
                          <FontAwesomeIcon icon={icon} style={{ color, fontSize: "0.95rem" }} />
                        </div>
                        <div style={{ fontSize: "0.72rem", fontWeight: 600, color: selected ? color : C.textDk, lineHeight: 1.3 }}>{value}</div>
                        <div style={{ width: 14, height: 14, borderRadius: "50%", border: `1.5px solid ${selected ? color : C.border}`, backgroundColor: selected ? color : "transparent", margin: "6px auto 0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {selected && <div style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#fff" }} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Priority & Assignment */}
            <div style={{ border: `1.5px solid ${C.border}`, borderRadius: 10, padding: "0.9rem 1rem" }}>
              <SH icon={faUsers} title="Priority & Assignment" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem" }}>
                <div>
                  <label style={lbl}>Priority Level</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["Normal", "Urgent"].map(v => {
                      const sel = priorityLevel === v;
                      const ac  = v === "Urgent" ? C.red : C.mid;
                      const abg = v === "Urgent" ? C.redBg : C.light;
                      return (
                        <div key={v} onClick={() => setPriorityLevel(v)} style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, padding: "0.45rem 0.75rem", borderRadius: 9, cursor: "pointer", border: `1.5px solid ${sel ? ac : C.border}`, backgroundColor: sel ? abg : C.white, fontWeight: 600, fontSize: "0.87rem", color: sel ? ac : C.textDk }}>
                          <div style={{ width: 14, height: 14, borderRadius: "50%", border: `1.5px solid ${sel ? ac : C.border}`, backgroundColor: sel ? ac : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {sel && <div style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#fff" }} />}
                          </div>
                          {v}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label style={lbl}>Assigned Expert / Officer</label>
                  <select value={assignedExpert} onChange={e => {
                    setAssignedExpert(e.target.value);
                    const u = allUsers.find(u => String(u._id) === e.target.value);
                    setAssignedExpertName(u ? u.fullName : '');
                  }} style={inp}>
                    <option value="">Select expert / officer</option>
                    {allUsers.map(u => <option key={u._id} value={String(u._id)}>{u.fullName}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Visit Details */}
            <div style={{ border: `1.5px solid ${C.border}`, borderRadius: 10, padding: "0.9rem 1rem" }}>
              <SH icon={faClipboardList} title="Visit Details" />
              <div style={{ marginBottom: "0.85rem" }}>
                <label style={lbl}>Visit Details (Short notes)</label>
                <textarea value={visitDetails} onChange={e => e.target.value.length <= 250 && setVisitDetails(e.target.value)} placeholder="Enter short notes about the visit..." rows={3} style={{ ...ta, width: "100%" }} />
                <div style={{ fontSize: "0.72rem", color: C.textGr, textAlign: "right" }}>{visitDetails.length}/250</div>
              </div>
              <div>
                <label style={lbl}>Crop Query (On Field)</label>
                <textarea value={cropQueryOnField} onChange={e => e.target.value.length <= 250 && setCropQueryOnField(e.target.value)} placeholder="Enter crop query observed on field..." rows={3} style={{ ...ta, width: "100%" }} />
                <div style={{ fontSize: "0.72rem", color: C.textGr, textAlign: "right" }}>{cropQueryOnField.length}/250</div>
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={resetForm} style={{ height: 38, padding: "0 18px", borderRadius: 9, border: `1.5px solid ${C.border}`, cursor: "pointer", fontSize: "0.87rem", fontWeight: 600, color: C.textDk, backgroundColor: C.white, display: "flex", alignItems: "center", gap: 7 }}>
                ↺ Cancel
              </button>
              <button onClick={handleSave} disabled={saving} style={{ height: 38, padding: "0 22px", borderRadius: 9, border: "none", cursor: saving ? "not-allowed" : "pointer", fontSize: "0.87rem", fontWeight: 700, backgroundColor: saving ? "#a0b8ab" : C.mid, color: "#fff", display: "flex", alignItems: "center", gap: 7 }}>
                {saving ? "Saving…" : "💾 Save Appointment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Scheduled Appointments ───────────────────────────────────────────── */}
      <InfoCard
        title="Scheduled Appointments"
        icon={faCalendarCheck}
        cols={1}
        action={
          <button
            onClick={() => setShowForm(f => !f)}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, backgroundColor: showForm ? C.bright : C.mid, color: "#fff", border: "none", borderRadius: 7, padding: "0.3rem 0.75rem", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}
          >
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: "0.6rem" }} />
            {showForm ? "Cancel" : "New Booking"}
          </button>
        }
      >
        <div style={{ gridColumn: "1 / -1" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "1.5rem", color: C.textGr, fontSize: "0.85rem" }}>Loading…</div>
          ) : current.length === 0 ? (
            <EmptyState icon={faCalendarAlt} message="No scheduled appointments" sub="Click New Booking to schedule one" />
          ) : (
            current.map((a, i) => <ApptRow key={a.id || i} appt={a} />)
          )}
        </div>
      </InfoCard>

      {/* ── Appointment History ──────────────────────────────────────────────── */}
      <InfoCard title="Appointment History" icon={faCalendarAlt} cols={1}>
        <div style={{ gridColumn: "1 / -1" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "1.5rem", color: C.textGr, fontSize: "0.85rem" }}>Loading…</div>
          ) : history.length === 0 ? (
            <EmptyState icon={faClipboardList} message="No past appointments" sub="Completed appointments will appear here" />
          ) : (
            history.slice(0, 10).map((a, i) => <ApptRow key={a.id || i} appt={a} />)
          )}
        </div>
      </InfoCard>
    </>
  );
};

// ─── TAB: Feedback ────────────────────────────────────────────────────────────
const VisitIcon = ({ color }) => (
  <svg viewBox="0 0 60 62" width="46" height="46" fill="none">
    <path d="M30 3C22.5 3 16 9.3 16 17c0 9.8 14 24 14 24s14-14.2 14-24C44 9.3 37.5 3 30 3z" fill={color}/>
    <circle cx="30" cy="17" r="5.5" fill="white"/>
    <circle cx="19" cy="46" r="5.5" fill={color}/>
    <path d="M8.5 60c0-5.8 4.7-10.5 10.5-10.5S29.5 54.2 29.5 60" fill={color}/>
    <circle cx="41" cy="46" r="5.5" fill={color}/>
    <path d="M30.5 60c0-5.8 4.7-10.5 10.5-10.5S51.5 54.2 51.5 60" fill={color}/>
  </svg>
);

const ProductIcon = ({ color }) => (
  <svg viewBox="0 0 56 64" width="44" height="44" fill="none">
    <rect x="20" y="4" width="16" height="7" rx="3.5" fill={color}/>
    <path d="M16 13 C11 18 10 24 10 30 L10 50 C10 54.4 13.6 58 18 58 L38 58 C42.4 58 46 54.4 46 50 L46 30 C46 24 45 18 40 13 Z" fill={color}/>
    <path d="M28 26 C28 26 19 29 19 39 C19 39 26 36 28 26Z" fill="white" opacity="0.85"/>
    <path d="M28 26 C28 26 37 29 37 39 C37 39 30 36 28 26Z" fill="white" opacity="0.65"/>
    <line x1="28" y1="26" x2="28" y2="44" stroke="white" strokeWidth="1.8" opacity="0.85" strokeLinecap="round"/>
  </svg>
);

const StaffIcon = ({ color }) => (
  <svg viewBox="0 0 62 60" width="46" height="46" fill="none">
    <circle cx="25" cy="17" r="11" fill={color}/>
    <path d="M4 55 C4 43 13.4 34 25 34 C36.6 34 46 43 46 55" fill={color}/>
    <circle cx="46" cy="38" r="11" fill={color} stroke="white" strokeWidth="2.5"/>
    <path d="M46 30.5l2.2 4.4 4.8.7-3.5 3.4.8 4.8-4.3-2.3-4.3 2.3.8-4.8-3.5-3.4 4.8-.7z" fill="#FFD700"/>
  </svg>
);

const FEEDBACK_TYPES = [
  { value: "Visit Related", Icon: VisitIcon, color: "#29553D" },
  { value: "Product Related", Icon: ProductIcon, color: "#1565c0" },
  { value: "Staff Related", Icon: StaffIcon, color: "#7b1fa2" },
];

const FEEDBACK_STATUSES = [
  { value: "Positive", emoji: "👍", color: "#2e7d32", bg: "#e8f5e9" },
  { value: "Negative", emoji: "👎", color: "#c62828", bg: "#fdecea" },
  { value: "Need follow-up", emoji: "🔄", color: "#e65100", bg: "#fff3e0" },
];

const FeedbackTab = ({ d, farmerID }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackType, setFeedbackType] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpTime, setFollowUpTime] = useState("");
  const [followUpAmPm, setFollowUpAmPm] = useState("AM");
  const [feedbackNote, setFeedbackNote] = useState("");

  const loadHistory = () => {
    if (!farmerID) { setLoading(false); return; }
    getFarmerFeedback(farmerID).then((res) => {
      setHistory(res?.data?.feedbacks || res?.data || []);
      setLoading(false);
    });
  };

  useEffect(() => { loadHistory(); }, [farmerID]);

  const resetForm = () => {
    setFeedbackType("");
    setFeedbackStatus("");
    setFollowUpDate("");
    setFollowUpTime("");
    setFollowUpAmPm("AM");
    setFeedbackNote("");
  };

  const handleSubmit = () => {
    if (!feedbackType || !feedbackStatus || !feedbackNote.trim()) return;
    setSubmitting(true);
    submitFarmerFeedback({
      farmerId: farmerID,
      farmerName: d?.fullName || d?.name || '',
      farmerMobile: d?.mobileNumber,
      feedbackType,
      feedbackStatus,
      followUpDate: feedbackStatus === "Need follow-up" ? followUpDate : "",
      followUpTime: feedbackStatus === "Need follow-up" ? followUpTime : "",
      followUpAmPm: feedbackStatus === "Need follow-up" ? followUpAmPm : "",
      feedbackNote,
    }).then((res) => {
      setSubmitting(false);
      if (res?.status === 200 || res?.status === 201) {
        resetForm();
        setLoading(true);
        loadHistory();
      }
    });
  };

  const canSubmit = !submitting && feedbackType && feedbackStatus && feedbackNote.trim();

  return (
    <>
      {/* ── Farmer Feedback Form ── */}
      <div
        style={{
          backgroundColor: C.white,
          borderRadius: 12,
          border: `1.5px solid ${C.cardBorder}`,
          overflow: "hidden",
          marginBottom: "0.75rem",
        }}
      >
        {/* Card header */}
        <div
          style={{
            backgroundColor: C.dark,
            padding: "0.6rem 1rem",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <FontAwesomeIcon
            icon={faCommentDots}
            style={{ fontSize: "0.65rem", color: "#fff" }}
          />
          <span
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "#fff",
              textTransform: "uppercase",
              letterSpacing: "0.09em",
            }}
          >
            Farmer Feedback
          </span>
        </div>

        <div style={{ padding: "1.25rem" }}>
          {/* ── Section 1: Feedback Type ── */}
          <div style={{ marginBottom: "1.25rem" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: "0.4rem",
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  backgroundColor: C.dark,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FontAwesomeIcon icon={faUserTag} style={{ fontSize: "0.7rem" }} />
              </div>
              <span
                style={{
                  fontSize: "0.88rem",
                  fontWeight: 700,
                  color: C.textDk,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Feedback Type
              </span>
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: C.textGr,
                marginBottom: "0.75rem",
                marginLeft: 34,
              }}
            >
              Please select the type of feedback
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "0.65rem",
              }}
            >
              {FEEDBACK_TYPES.map((opt) => {
                const selected = feedbackType === opt.value;
                return (
                  <div
                    key={opt.value}
                    onClick={() => setFeedbackType(opt.value)}
                    style={{
                      border: `2px solid ${selected ? opt.color : C.border}`,
                      borderRadius: 10,
                      padding: "0.9rem 0.5rem 0.75rem",
                      textAlign: "center",
                      cursor: "pointer",
                      backgroundColor: selected ? `${opt.color}0d` : C.white,
                      transition: "all 0.15s",
                      position: "relative",
                    }}
                  >
                    <input
                      type="radio"
                      readOnly
                      checked={selected}
                      style={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        accentColor: opt.color,
                      }}
                    />
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
                      <opt.Icon color={opt.color} />
                    </div>
                    <div
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: opt.color,
                      }}
                    >
                      {opt.value}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Section 2: Feedback Status ── */}
          <div style={{ marginBottom: "1.25rem" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: "0.4rem",
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  backgroundColor: C.dark,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: "0.7rem" }} />
              </div>
              <span
                style={{
                  fontSize: "0.88rem",
                  fontWeight: 700,
                  color: C.textDk,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Feedback Status
              </span>
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: C.textGr,
                marginBottom: "0.75rem",
                marginLeft: 34,
              }}
            >
              Please select the feedback status
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "0.65rem",
              }}
            >
              {FEEDBACK_STATUSES.map((opt) => {
                const selected = feedbackStatus === opt.value;
                return (
                  <div
                    key={opt.value}
                    onClick={() => setFeedbackStatus(opt.value)}
                    style={{
                      border: `2px solid ${selected ? opt.color : C.border}`,
                      borderRadius: 10,
                      padding: "0.9rem 0.5rem 0.75rem",
                      textAlign: "center",
                      cursor: "pointer",
                      backgroundColor: selected ? opt.bg : C.white,
                      transition: "all 0.15s",
                      position: "relative",
                    }}
                  >
                    <input
                      type="radio"
                      readOnly
                      checked={selected}
                      style={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        accentColor: opt.color,
                      }}
                    />
                    <div style={{ fontSize: "2rem", marginBottom: 5 }}>
                      {opt.emoji}
                    </div>
                    <div
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: opt.color,
                      }}
                    >
                      {opt.value}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Follow-up Details */}
            {feedbackStatus === "Need follow-up" && (
              <div
                style={{
                  marginTop: "0.85rem",
                  border: `1.5px solid #e65100`,
                  borderRadius: 10,
                  padding: "1rem",
                  backgroundColor: "#fff8f5",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: "0.85rem",
                  }}
                >
                  <span style={{ fontSize: "1rem" }}>📅</span>
                  <span
                    style={{
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: "#e65100",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Follow-up Details
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0.75rem",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: C.textDk,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Follow-up Date{" "}
                      <span style={{ color: C.red }}>*</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          left: 9,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "0.85rem",
                          pointerEvents: "none",
                        }}
                      >
                        📅
                      </span>
                      <input
                        type="date"
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                        style={{
                          width: "100%",
                          paddingLeft: 30,
                          paddingRight: 8,
                          paddingTop: 8,
                          paddingBottom: 8,
                          borderRadius: 8,
                          border: `1.5px solid ${C.border}`,
                          fontSize: "0.82rem",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: C.textDk,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Follow-up Time{" "}
                      <span style={{ color: C.red }}>*</span>
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "center",
                      }}
                    >
                      <div style={{ position: "relative", flex: 1 }}>
                        <span
                          style={{
                            position: "absolute",
                            left: 9,
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: "0.85rem",
                            pointerEvents: "none",
                          }}
                        >
                          🕐
                        </span>
                        <input
                          type="time"
                          value={followUpTime}
                          onChange={(e) => setFollowUpTime(e.target.value)}
                          style={{
                            width: "100%",
                            paddingLeft: 30,
                            paddingRight: 8,
                            paddingTop: 8,
                            paddingBottom: 8,
                            borderRadius: 8,
                            border: `1.5px solid ${C.border}`,
                            fontSize: "0.82rem",
                            outline: "none",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                      <div
                        style={{ display: "flex", gap: 8, flexShrink: 0 }}
                      >
                        {["AM", "PM"].map((ap) => (
                          <label
                            key={ap}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                              cursor: "pointer",
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              color: C.textDk,
                            }}
                          >
                            <input
                              type="radio"
                              name="followup-ampm"
                              value={ap}
                              checked={followUpAmPm === ap}
                              onChange={() => setFollowUpAmPm(ap)}
                              style={{ accentColor: C.dark }}
                            />
                            {ap}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "0.72rem",
                    color: "#e65100",
                    marginTop: "0.5rem",
                    fontStyle: "italic",
                  }}
                >
                  (This section is required only if you select "Need
                  follow-up")
                </div>
              </div>
            )}
          </div>

          {/* ── Section 3: Feedback Note ── */}
          <div style={{ marginBottom: "1.1rem" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: "0.4rem",
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  backgroundColor: C.dark,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FontAwesomeIcon icon={faPen} style={{ fontSize: "0.7rem" }} />
              </div>
              <span
                style={{
                  fontSize: "0.88rem",
                  fontWeight: 700,
                  color: C.textDk,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Feedback Note
              </span>
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: C.textGr,
                marginBottom: "0.75rem",
                marginLeft: 34,
              }}
            >
              Please provide your feedback / comments
            </div>
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: 10,
                  top: 11,
                  fontSize: "0.85rem",
                  color: C.textGr,
                  pointerEvents: "none",
                }}
              >
                ✏️
              </span>
              <textarea
                value={feedbackNote}
                onChange={(e) => {
                  if (e.target.value.length <= 500)
                    setFeedbackNote(e.target.value);
                }}
                placeholder="Write your feedback here..."
                style={{
                  width: "100%",
                  height: 110,
                  paddingLeft: 32,
                  paddingRight: 12,
                  paddingTop: 10,
                  paddingBottom: 28,
                  borderRadius: 8,
                  border: `1.5px solid ${C.border}`,
                  fontSize: "0.85rem",
                  resize: "vertical",
                  outline: "none",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  color: C.textDk,
                }}
              />
              <span
                style={{
                  position: "absolute",
                  bottom: 8,
                  right: 10,
                  fontSize: "0.7rem",
                  color: C.textGr,
                }}
              >
                {feedbackNote.length} / 500
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={resetForm}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "0.55rem 1.25rem",
                borderRadius: 8,
                border: `1.5px solid ${C.border}`,
                backgroundColor: C.white,
                fontSize: "0.82rem",
                fontWeight: 600,
                cursor: "pointer",
                color: C.textDk,
              }}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: "0.75rem" }} />
              CANCEL
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "0.55rem 1.35rem",
                borderRadius: 8,
                border: "none",
                backgroundColor: C.dark,
                fontSize: "0.82rem",
                fontWeight: 600,
                cursor: canSubmit ? "pointer" : "not-allowed",
                color: "#fff",
                opacity: canSubmit ? 1 : 0.45,
              }}
            >
              <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: "0.75rem" }} />
              {submitting ? "Submitting…" : "SUBMIT FEEDBACK"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Farmer Feedback History ── */}
      <InfoCard title="Farmer Feedback History" icon={faClipboardList} cols={1}>
        <div style={{ gridColumn: "1 / -1" }}>
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "1.5rem",
                color: C.textGr,
                fontSize: "0.85rem",
              }}
            >
              Loading…
            </div>
          ) : history.length === 0 ? (
            <EmptyState
              icon={faStar}
              message="No farmer feedback yet"
              sub="Submitted feedback will appear here"
            />
          ) : (
            <>
              {history.map((fb, i) => {
                const statusMeta = FEEDBACK_STATUSES.find(
                  (s) => s.value === (fb.feedbackStatus || fb.status),
                );
                const typeMeta = FEEDBACK_TYPES.find(
                  (t) => t.value === (fb.feedbackType || fb.type),
                );
                return (
                  <div
                    key={i}
                    style={{
                      padding: "0.65rem 0.75rem",
                      borderRadius: 9,
                      marginBottom: 6,
                      backgroundColor: C.white,
                      border: `1px solid ${C.cardBorder}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 5,
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {typeMeta && (
                          <span
                            style={{
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              padding: "2px 8px",
                              borderRadius: 8,
                              backgroundColor: `${typeMeta.color}15`,
                              color: typeMeta.color,
                            }}
                          >
                            {fb.feedbackType || fb.type}
                          </span>
                        )}
                        {statusMeta && (
                          <span
                            style={{
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              padding: "2px 8px",
                              borderRadius: 8,
                              backgroundColor: statusMeta.bg,
                              color: statusMeta.color,
                            }}
                          >
                            {statusMeta.emoji} {fb.feedbackStatus || fb.status}
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: "0.68rem", color: "#aab8b0" }}>
                        {moment(fb.createdAt || fb.date).format("DD MMM YYYY, hh:mm A")}
                      </span>
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "0.82rem",
                        color: C.textGr,
                        lineHeight: 1.5,
                      }}
                    >
                      {fb.feedbackNote || fb.note}
                    </p>
                    {(fb.feedbackStatus || fb.status) === "Need follow-up" && fb.followUpDate && (
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "#e65100",
                          marginTop: 5,
                          fontWeight: 600,
                        }}
                      >
                        📅 Follow-up: {fb.followUpDate} at {fb.followUpTime}{" "}
                        {fb.followUpAmPm}
                      </div>
                    )}
                    {fb.submittedBy && (
                      <div style={{ fontSize: "0.68rem", color: "#aab8b0", marginTop: 3 }}>
                        By: {fb.submittedBy}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </InfoCard>
    </>
  );
};

// ─── TAB: Soil Testing ────────────────────────────────────────────────────────
const SoilTestingTab = ({ d, farmerId }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  // pdfBlob: local blob: URL used for <embed> — works regardless of server auth/CORS
  const [pdfBlob, setPdfBlob]     = useState(null);
  // serverUrl: full URL for download link and <object> fallback
  const [serverUrl, setServerUrl] = useState(null);
  // 'idle' | 'loading' | 'ready' | 'failed'
  const [fetchState, setFetchState] = useState('idle');
  const blobRef   = useRef(null);
  const fileRef   = useRef(null);

  // Revoke old blob URL and apply new one
  const applyBlob = useCallback((url) => {
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    blobRef.current = url;
    setPdfBlob(url);
    setFetchState('ready');
  }, []);

  // Cleanup on unmount
  useEffect(() => () => { if (blobRef.current) URL.revokeObjectURL(blobRef.current); }, []);

  // When farmer data loads: fetch PDF via axios so JWT auth header is sent automatically.
  // encodeURI fixes filenames with spaces (e.g. "soil report 4123.pdf" → "soil%20report%204123.pdf").
  useEffect(() => {
    if (!d?.soilReportUrl) return;
    const raw = d.soilReportUrl.startsWith('http')
      ? d.soilReportUrl
      : `${process.env.REACT_APP_API_URL}${d.soilReportUrl}`;
    const full = encodeURI(raw);
    setServerUrl(full);
    setFetchState('loading');
    let cancelled = false;
    axios.get(full, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        const ct = res.headers['content-type'] || '';
        // If server returns HTML (SPA catch-all / file not found), don't treat it as PDF
        if (ct.includes('text/html')) throw new Error('html-response');
        applyBlob(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })));
      })
      .catch(() => { if (!cancelled) setFetchState('failed'); });
    return () => { cancelled = true; };
  }, [d?.soilReportUrl, applyBlob]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setUploadErr('');
    applyBlob(URL.createObjectURL(file)); // instant local preview — no server needed
    setUploading(true);
    try {
      const form = new FormData();
      form.append('farmerId', farmerId);
      form.append('pdf', file);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/farmer/uploadSoilReport`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const saved = res.data.url;
      const raw = saved?.startsWith('http') ? saved : `${process.env.REACT_APP_API_URL}${saved}`;
      setServerUrl(encodeURI(raw));
    } catch {
      setUploadErr('Upload failed — PDF is shown but may not be saved. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const btn = (primary, extra = {}) => ({
    fontSize: "0.72rem", fontWeight: 600, padding: "4px 12px", borderRadius: 7,
    border: `1px solid ${C.mid}`, cursor: "pointer",
    backgroundColor: primary ? C.mid : C.light,
    color: primary ? "#fff" : C.mid,
    ...extra,
  });

  const hasReport = !!(pdfBlob || serverUrl);

  return (
    <>
      <InfoCard title="Detailed Soil Report" icon={faClipboardList} cols={1}>
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={{ border: `2px dashed ${C.cardBorder}`, borderRadius: 10, overflow: "hidden", backgroundColor: C.white }}>

            {/* ── Header ── */}
            <div style={{ backgroundColor: "#f4f7f5", borderBottom: `1px solid ${C.cardBorder}`, padding: "0.65rem 1rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem", fontWeight: 600, color: C.textGr }}>
                <span style={{ fontSize: "1rem" }}>📄</span> farmer_soil_report.pdf
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {serverUrl && (
                  <a href={serverUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                    <button style={btn(false)}>⬇ Download</button>
                  </a>
                )}
                <input type="file" accept=".pdf,application/pdf" ref={fileRef} style={{ display: "none" }} onChange={handleUpload} />
                <button
                  style={btn(true, { opacity: uploading ? 0.65 : 1 })}
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? "Uploading…" : hasReport ? "🔄 Replace PDF" : "⬆ Upload PDF"}
                </button>
                <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "3px 9px", borderRadius: 8, backgroundColor: hasReport ? C.light : C.orangeBg, color: hasReport ? C.bright : C.orange }}>
                  {hasReport ? "Uploaded" : "Not Uploaded"}
                </span>
              </div>
            </div>

            {/* ── Upload error ── */}
            {uploadErr && (
              <div style={{ backgroundColor: C.redBg, color: C.red, fontSize: "0.78rem", padding: "0.5rem 1rem", borderBottom: `1px solid ${C.red}` }}>
                {uploadErr}
              </div>
            )}

            {/* ── PDF area ── */}
            {fetchState === 'loading' ? (
              <div style={{ height: 540, display: "flex", alignItems: "center", justifyContent: "center", color: C.textGr, fontSize: "0.85rem" }}>
                Loading PDF…
              </div>

            ) : pdfBlob ? (
              /* Blob URL: always works — browser-local, no auth needed */
              <embed key={pdfBlob} src={pdfBlob} type="application/pdf" width="100%" height="540" style={{ display: "block" }} />

            ) : serverUrl ? (
              /* Blob fetch failed (CORS from dev/localhost). Never use <object> with an SPA server —
                 it returns index.html for unknown paths and breaks with cascading JS/CSS errors.
                 Show a simple card with an "Open" button instead. Works reliably in all environments. */
              <div style={{ height: 360, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, backgroundColor: "#fafcfb" }}>
                <span style={{ fontSize: "3.5rem" }}>📄</span>
                <div style={{ fontSize: "0.95rem", fontWeight: 700, color: C.textGr }}>Soil Report Uploaded</div>
                <div style={{ fontSize: "0.78rem", color: "#aab8b0", textAlign: "center", maxWidth: 300, lineHeight: 1.6 }}>
                  PDF preview is not available when accessing from a different domain.<br />
                  Click below to open or download the report.
                </div>
                <a href={serverUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                  <button style={{ ...btn(true), fontSize: "0.85rem", padding: "8px 24px", borderRadius: 8 }}>
                    Open PDF in New Tab
                  </button>
                </a>
              </div>

            ) : (
              /* No PDF uploaded yet */
              <div style={{ height: 360, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.75rem", backgroundColor: "#fafcfb", padding: "2rem" }}>
                <div style={{ width: 64, height: 80, borderRadius: 6, backgroundColor: C.redBg, border: `2px solid ${C.red}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, position: "relative" }}>
                  <div style={{ position: "absolute", top: -1, right: -1, width: 0, height: 0, borderStyle: "solid", borderWidth: "0 14px 14px 0", borderColor: `transparent ${C.red} transparent transparent` }} />
                  <span style={{ fontSize: "1.4rem" }}>📋</span>
                  <span style={{ fontSize: "0.55rem", fontWeight: 800, color: C.red, letterSpacing: "0.05em" }}>PDF</span>
                </div>
                <div style={{ fontSize: "0.9rem", fontWeight: 700, color: C.textGr }}>Soil Test Report PDF</div>
                <div style={{ fontSize: "0.78rem", color: "#aab8b0", textAlign: "center", maxWidth: 300, lineHeight: 1.55 }}>
                  Click <strong>Upload PDF</strong> to attach the farmer's soil test report.
                </div>
                <button
                  style={{ ...btn(true), fontSize: "0.82rem", padding: "8px 22px", borderRadius: 8, marginTop: "0.25rem" }}
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? "Uploading…" : "⬆ Upload PDF"}
                </button>
              </div>
            )}

          </div>
        </div>
      </InfoCard>
    </>
  );
};

// ─── Main Drawer ──────────────────────────────────────────────────────────────
const FarmerDrawer = ({
  farmer,
  allFarmers = [],
  onClose,
  onFarmerUpdated,
}) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);

  const reloadDetails = () => {
    getFarmerDetails(farmer.mobileNumber).then((res) => {
      if (res?.status === 200) setDetails(res.data);
    });
  };

  useEffect(() => {
    setLoading(true);
    setDetails(null);
    setActiveTab("profile");
    getFarmerDetails(farmer.mobileNumber).then((res) => {
      if (res?.status === 200) setDetails(res.data);
      setLoading(false);
    });
  }, [farmer.mobileNumber]);

  const d = details ? { ...farmer, ...details } : farmer;
  const farmerID = d?._id || farmer?._id;

  return (
    <>
      {/* Backdrop */}
      {!showEdit && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1100,
            backgroundColor: "rgba(15,30,20,0.52)",
            backdropFilter: "blur(2px)",
          }}
        />
      )}

      {/* Panel */}
      <div
        style={{
          display: showEdit ? "none" : "flex",
          flexDirection: "column",
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1200,
          width: "min(920px, 96vw)",
          backgroundColor: "#fff",
          boxShadow: "-6px 0 48px rgba(0,0,0,0.22)",
          animation: "drawerIn 0.2s cubic-bezier(.4,0,.2,1)",
        }}
      >
        <style>{`
          @keyframes drawerIn {
            from { transform: translateX(100%); opacity: 0.6; }
            to   { transform: translateX(0);    opacity: 1; }
          }
        `}</style>

        {/* ── Header ── */}
        <div style={{ backgroundColor: C.dark, flexShrink: 0 }}>
          <div style={{ padding: "0.9rem 1.25rem 0.9rem" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.8rem",
                marginBottom: "0.8rem",
              }}
            >
              {/* Avatar */}
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  flexShrink: 0,
                  backgroundColor: C.mid,
                  border: "2px solid #3d8c5e",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1rem",
                  fontWeight: 800,
                }}
              >
                {initials(farmer.fullName)}
              </div>

              {/* Name + location */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "#fff",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {farmer.fullName || "—"}
                </div>
                <div
                  style={{
                    fontSize: "0.72rem",
                    color: "rgba(255,255,255,0.55)",
                    marginTop: 2,
                  }}
                >
                  📍{" "}
                  {[farmer.townVillage, farmer.district, farmer.state]
                    .filter(Boolean)
                    .join(" · ") || "Location not set"}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button
                  onClick={() => setShowEdit(true)}
                  style={hdrBtn(C.bright, "#fff")}
                >
                  ✏️ Edit
                </button>
                <a
                  href={`tel:${farmer.mobileNumber}`}
                  style={{ textDecoration: "none" }}
                >
                  <button style={hdrBtn(C.gold, "#fff")}>📞 Call</button>
                </a>
                <button
                  onClick={onClose}
                  style={{
                    ...hdrBtn("rgba(255,255,255,0.12)", "#fff"),
                    padding: "0 10px",
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>
          </div>
          {/* Tabs — own bar, separate from dark header */}
          <div
            style={{
              display: "flex",
              gap: 0,
              overflowX: "auto",
              backgroundColor: C.white,
              borderBottom: `2px solid ${C.cardBorder}`,
              padding: "0 1.25rem",
            }}
          >
            {TABS.map(({ key, label, icon }) => {
              const active = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    background: "none",
                    border: "none",
                    outline: "none",
                    cursor: "pointer",
                    padding: "0.6rem 0.9rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: "0.8rem",
                    fontWeight: active ? 700 : 500,
                    color: active ? C.dark : C.textGr,
                    borderBottom: "none",
                    boxShadow: active ? `inset 0 -3px 0 ${C.dark}` : "none",
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                  }}
                >
                  {typeof icon === "string" ? (
                    <span style={{ fontSize: "0.85rem" }}>{icon}</span>
                  ) : (
                    <FontAwesomeIcon
                      icon={icon}
                      style={{
                        fontSize: "0.7rem",
                        color: active ? C.dark : C.textGr,
                      }}
                    />
                  )}
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1rem 1.25rem" }}>
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "4rem 0",
                color: C.textGr,
              }}
            >
              <div style={{ fontSize: "0.9rem" }}>Loading farmer details…</div>
            </div>
          ) : activeTab === "profile" ? (
            <ProfileTab d={d} />
          ) : activeTab === "nearby" ? (
            <NearbyTab d={d} allFarmers={allFarmers} />
          ) : activeTab === "crops" ? (
            <CropsTab d={d} farmerID={farmerID} />
          ) : activeTab === "appointments" ? (
            <AppointmentsTab d={d} farmerID={farmerID} />
          ) : activeTab === "feedback" ? (
            <FeedbackTab d={d} farmerID={farmerID} />
          ) : activeTab === "soil" ? (
            <SoilTestingTab d={d} farmerId={farmerID} />
          ) : null}
        </div>
      </div>

      {showEdit && (
        <FarmerFormDrawer
          mode="edit"
          farmer={d}
          onClose={() => setShowEdit(false)}
          onSuccess={(updatedFarmer) => {
            setShowEdit(false);
            reloadDetails();
            if (onFarmerUpdated) onFarmerUpdated(updatedFarmer);
          }}
        />
      )}
    </>
  );
};

const hdrBtn = (bg, color) => ({
  height: 32,
  padding: "0 12px",
  borderRadius: 7,
  border: "none",
  backgroundColor: bg,
  color,
  fontSize: "0.8rem",
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
});

export default FarmerDrawer;
